import React from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  Tab as MuiTab,
  TabProps,
  Tabs as MuiTabs,
  TabsProps,
  Typography,
  TypographyTypes,
  Color,
  units,
} from '@hbf/dsl';

export interface TabsContent {
  text?: React.ReactNode;
  icon?: JSX.Element;
  tabProps?: {
    component: React.ComponentClass;
  } & TabProps;
}

export interface CustomTabs {
  style?: 'light' | 'dark';
  tabsContent: TabsContent[];
  onChange?: (
    event: React.ChangeEvent<HTMLButtonElement>,
    value: number,
  ) => void;
  tabsClasses?: { [key: string]: string };
  tabClasses?: { [key: string]: string };
  textTransform?: 'none' | 'uppercase';
  typographyType?: TypographyTypes;
  selectedLabelColor?: Color;
  unselectedLabelColor?: Color | string;
  typographyClass?: string;
}

const useStyles = makeStyles()(t => ({
  tabsRootLight: {
    backgroundColor: t.palette.background.paper,
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.4)',
    zIndex: t.zIndex.tooltip,
  },
  tabsRootDark: {
    color: t.palette.background.paper,
    backgroundColor: 'black',
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.4)',
    zIndex: t.zIndex.tooltip,
  },
  tabsIndicator: {
    backgroundColor: t.palette.secondary.main,
    height: 4,
  },
  labelIcon: {
    color: '#aab4bb', // grey 250 of new palette
    '& svg': {
      paddingBottom: units.rem(0.5),
      paddingTop: units.rem(0.5),
      boxSizing: 'content-box',
    },
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  labelText: {
    paddingLeft: t.spacing(1),
    paddingRight: t.spacing(1),
  },
  selectedTabLight: {
    color: t.palette.grey[800],
  },
  selectedTabDark: {
    color: t.palette.common.white,
  },
}));

export type Props = CustomTabs & Omit<TabsProps, 'onChange' | 'style'>;

const Tabs: React.FC<Props> = ({
  style = 'light',
  tabsContent,
  tabsClasses = {},
  tabClasses = {},
  textTransform = 'uppercase',
  typographyType = TypographyTypes.HelperText,
  selectedLabelColor,
  unselectedLabelColor,
  typographyClass,
  ...props
}) => {
  const { classes, cx } = useStyles();

  const MuiTypography = Typography[typographyType];

  const getLabelColor = React.useCallback(
    (isTabSelected: boolean) => {
      if (isTabSelected && selectedLabelColor) {
        return selectedLabelColor;
      }

      return style === 'light'
        ? (unselectedLabelColor as Color) || 'muted'
        : 'white';
    },
    [selectedLabelColor, unselectedLabelColor, style],
  );

  return (
    <MuiTabs
      classes={{
        indicator: classes.tabsIndicator,
        ...tabsClasses,
        root: cx(
          style === 'light' ? classes.tabsRootLight : classes.tabsRootDark,
          tabsClasses.root,
        ),
      }}
      {...props}
    >
      {tabsContent.map((tab, i) => (
        <MuiTab
          {...tab.tabProps}
          classes={{
            selected:
              style === 'light'
                ? classes.selectedTabLight
                : classes.selectedTabDark,
            ...tabClasses,
            root: cx(classes.labelIcon, tabClasses.root),
          }}
          label={
            tab.text && (
              <MuiTypography
                className={cx(
                  classes.labelText,
                  typographyClass && typographyClass,
                )}
                color={getLabelColor(props.value === i)}
                textTransform={textTransform}
              >
                {tab.text}
              </MuiTypography>
            )
          }
          icon={tab.icon}
          key={i}
          data-test-locator={`MuiTabs - Tab_${i}`}
        />
      ))}
    </MuiTabs>
  );
};

export { Tabs };
